import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [filter, setFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolioData();
  }, []);

  useEffect(() => {
    fetchPortfolioItems();
  }, [filter]);

  const fetchPortfolioData = async () => {
    try {
      // Fetch categories
      const categoriesResponse = await axios.get(`${API}/portfolio/categories`);
      setCategories(categoriesResponse.data.categories);
      
      // Fetch initial portfolio items
      await fetchPortfolioItems();
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolioItems = async () => {
    try {
      const params = filter !== 'All' ? { category: filter } : {};
      const response = await axios.get(`${API}/portfolio`, { params });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching portfolio items:', error);
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="text-gray-900 text-xl">Loading portfolio... 🔄</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden" id="portfolio">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <Badge className="bg-purple-100 text-purple-800 border-purple-200 mb-4 px-4 py-2">
              Portfolio
            </Badge>
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6">
              Dekh Le Kya Banaya Hai! 🎨
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Recent projects jo dikhati hai ki apun kitna swag level ka kaam karta hai! From startups to established brands - sabka portfolio hai! 🚀
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={filter === category ? "default" : "outline"}
                onClick={() => setFilter(category)}
                className={`px-6 py-3 font-semibold transition-all duration-300 ${
                  filter === category
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'border-2 border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600 hover:scale-105'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <Card 
                key={project.id}
                className="group bg-white hover:bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 cursor-pointer overflow-hidden"
                onClick={() => setSelectedProject(project)}
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-black/20 backdrop-blur-sm text-white border-0">
                      {project.category}
                    </Badge>
                  </div>
                  {project.featured && (
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-yellow-500 text-yellow-900 border-0">
                        ⭐ Featured
                      </Badge>
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4 right-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
                    <Button 
                      size="sm" 
                      className="bg-white text-gray-900 hover:bg-gray-100 w-full"
                    >
                      View Details 👀
                    </Button>
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, idx) => (
                      <Badge 
                        key={idx} 
                        variant="secondary" 
                        className="bg-blue-100 text-blue-800 text-xs px-2 py-1"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {projects.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">No projects found for this category. 🤔</div>
            </div>
          )}

          {/* View More Button */}
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 text-lg font-semibold transform hover:scale-105 transition-all duration-300 shadow-xl"
            >
              Complete Portfolio Dekhna Hai? 🔍
            </Button>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: `${projects.length}+`, label: 'Projects Completed' },
              { number: '30+', label: 'Happy Clients' },
              { number: '10+', label: 'Industries Served' },
              { number: '100%', label: 'Satisfaction Rate' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium text-sm">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProject(null)}
        >
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full transform transition-all duration-300">
            <img 
              src={selectedProject.image} 
              alt={selectedProject.title}
              className="w-full h-64 object-cover rounded-lg mb-4"
            />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {selectedProject.title}
            </h3>
            <div className="mb-3">
              <Badge className="bg-blue-100 text-blue-800">
                {selectedProject.category}
              </Badge>
              {selectedProject.featured && (
                <Badge className="bg-yellow-100 text-yellow-800 ml-2">
                  ⭐ Featured
                </Badge>
              )}
            </div>
            <p className="text-gray-600 mb-4">
              {selectedProject.description}
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedProject.tags.map((tag, idx) => (
                <Badge key={idx} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
            <Button 
              onClick={() => setSelectedProject(null)}
              className="w-full bg-gray-900 hover:bg-gray-800"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Portfolio;